/**
 * regioncoverage/helpers.js
 *
 * RegionCoverage 페이지에서 사용하는 모든 상수 및 헬퍼 함수를 모아둔 모듈입니다.
 * - 지도 초기값 상수 (기간, 중심 좌표, 줌, 색상 기준)
 * - 기회도 색상/레이블/마커 크기 계산 함수
 * - Leaflet divIcon 생성 함수
 * - 타임라인 문자 배열 생성 함수
 * - 겹침 해소 최소 줌 계산 함수
 */
import { todayKST, toDateOnlyUtc } from '../../../utils/date';

import L from 'leaflet';

// ─── 상수 ────────────────────────────────────────────────────────────────────

/** 방문일수 집계 기간 (일) – 고정값, UI 선택 없음 */
export const DEFAULT_PERIOD = 30;

/** 대한민국 중심 좌표 (위도, 경도) */
export const KOREA_CENTER = [36.5, 127.8];

/** 기본 줌 레벨 */
export const DEFAULT_ZOOM = 7;

/**
 * 색상 판단 기준 기본값 (DB 설정이 없을 때 사용)
 * TicketTypeManagement의 "지역 색상 기준" 섹션에서 동적으로 변경 가능합니다.
 *
 * day1~day4: 각 색상 구간의 경과일 임계값
 * visit2~visitMonth: 각 구간에서 상위 색상으로 격상되는 방문일수 임계값
 */
export const DEFAULT_COLOR_RULES = {
  day1:       7,  // < day1일 → 🔴 빨강
  day2:       14, // < day2일 기본 → 🟠 주황 (방문일수 > visit2이면 🔴)
  day3:       21, // < day3일 기본 → 🟡 노랑 (방문일수 > visit3이면 🟠)
  day4:       28, // < day4일 기본 → 🟢 초록 (방문일수 > visit4이면 🟡)
  visit2:     3,  // day2 구간 빨강 격상 임계값
  visit3:     4,  // day3 구간 주황 격상 임계값
  visit4:     4,  // day4 구간 노랑 격상 임계값
  visitMonth: 5,  // day4 이후 초록 격상 임계값 (미만이면 🔵 파랑)
};

// ─── 색상 / 크기 헬퍼 ────────────────────────────────────────────────────────

/**
 * 마지막 방문 경과일 + 기간 내 방문일수 → 기회도 색상 반환
 *
 * 기본 색상은 경과일로 결정하되, 방문 빈도가 높으면 더 경고색으로 격상합니다.
 * 색상 판단 기준(rules)은 DB에서 동적으로 불러오며, 없으면 DEFAULT_COLOR_RULES 사용.
 *
 * @param {number|null} daysSinceLast     마지막 방문 경과일
 * @param {number}      visitDaysInPeriod 기간 내 방문일수
 * @param {object}      [rules]           색상 기준 객체 (DEFAULT_COLOR_RULES 형식)
 * @returns {string} CSS 색상 문자열 (hex)
 */
export function getOpportunityColor(daysSinceLast, visitDaysInPeriod, rules) {
  const d = daysSinceLast     != null ? Number(daysSinceLast)     : 999;
  const v = visitDaysInPeriod != null ? Number(visitDaysInPeriod) : 0;
  const r = rules || DEFAULT_COLOR_RULES; // 동적 규칙, 없으면 기본값

  if (d < r.day1)  return '#ef4444'; // 빨강: day1일 이내 방문

  if (d < r.day2) {                              // day2일 이내
    if (v > r.visit2) return '#ef4444';          // 빨강: 방문일수 visit2 초과
    return '#f97316';                            // 주황
  }

  if (d < r.day3) {                              // day3일 이내
    if (v > r.visit3) return '#f97316';          // 주황: 방문일수 visit3 초과
    return '#eab308';                            // 노랑
  }

  if (d < r.day4) {                              // day4일 이내
    if (v > r.visit4) return '#eab308';          // 노랑: 방문일수 visit4 초과
    return '#22c55e';                            // 초록
  }

  // day4일 초과
  if (v > r.visitMonth) return '#22c55e';        // 초록: 방문일수 visitMonth 초과
  return '#3b82f6';                              // 파랑: 방문 강력 추천
}

/**
 * 기회도 한국어 레이블 반환
 *
 * @param {number|null} daysSinceLast     마지막 방문 경과일
 * @param {number}      visitDaysInPeriod 기간 내 방문일수
 * @param {object}      [rules]           색상 기준 객체
 * @returns {string} '방문포화' | '주의' | '보통' | '방문추천' | '강력추천'
 */
export function getOpportunityLabel(daysSinceLast, visitDaysInPeriod, rules) {
  const color = getOpportunityColor(daysSinceLast, visitDaysInPeriod, rules);
  if (color === '#ef4444') return '방문포화';
  if (color === '#f97316') return '주의';
  if (color === '#eab308') return '보통';
  if (color === '#22c55e') return '방문추천';
  return '강력추천'; // 파랑 (#3b82f6)
}

/**
 * 기간 내 방문일수 → 마커 지름(px)
 * 방문일수가 많을수록 마커를 크게 표시하여 포화 정도를 직관적으로 나타냅니다.
 *
 * @param {number} visitDays 기간 내 방문일수
 * @returns {number} 마커 지름 픽셀
 */
export function getMarkerSize(visitDays) {
  if (visitDays <= 0) return 12;
  if (visitDays <= 2) return 15;
  if (visitDays <= 5) return 17;
  if (visitDays <= 9) return 20;
  return 21;
}

/**
 * Leaflet divIcon 생성
 * - 원형 점: 색상(기회도) + 크기(방문빈도)
 * - 텍스트 없음, 기본 테두리 없음
 * - isHighlighted=true 시 흰 테두리 + 링 효과로 강조 표시
 *
 * @param {number|null} daysSinceLast  마지막 방문 경과일
 * @param {number}      visitDays      기간 내 방문일수
 * @param {boolean}     isHighlighted  클릭(강조) 여부
 * @param {object}      [rules]        색상 기준 객체
 * @returns {L.DivIcon} Leaflet divIcon 인스턴스
 */
export function createRegionIcon(daysSinceLast, visitDays, isHighlighted, rules) {
  const color = getOpportunityColor(daysSinceLast, visitDays, rules);
  const size  = getMarkerSize(visitDays);

  // 클릭(강조) 상태일 때만 테두리 + 외곽 링 표시
  const border      = isHighlighted ? `2px solid #fff` : 'none';
  const shadowExtra = isHighlighted ? `, 0 0 0 2px ${color}` : '';

  return L.divIcon({
    className: '',
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `
      <div style="
        width:${size}px; height:${size}px;
        border-radius:50%;
        background:${color};
        border:${border};
        box-shadow:0 1px 4px rgba(0,0,0,0.3)${shadowExtra};
        cursor:pointer;
      "></div>
    `,
  });
}

/**
 * 실제 방문 날짜 배열 → 30칸 타임라인 문자 배열
 *
 * visitDates: ['2026-03-12', '2026-03-17', ...] (API visit_dates_in_period)
 * 반환: 길이 30 배열, 인덱스 0 = 30일 전, 인덱스 29 = 오늘
 * 방문 있는 날 → ':', 없는 날 → '.'
 *
 * @param {string[]} visitDates ISO 날짜 문자열 배열
 * @returns {string[]} 길이 30 문자 배열 (':' 또는 '.')
 */
export function buildTimelineChars(visitDates) {
  const chars = Array(30).fill('.');
  if (!visitDates || visitDates.length === 0) return chars;

  // toDateOnlyUtc, todayKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const today = toDateOnlyUtc(todayKST());

  for (const dateStr of visitDates) {
    const d = toDateOnlyUtc(dateStr);
    if (!d) continue;
    // 경과일: 오늘=0, 1일전=1, ..., 30일전=30
    const daysAgo = Math.round((today - d) / (1000 * 60 * 60 * 24));
    if (daysAgo < 0 || daysAgo > 30) continue;
    // 인덱스: 30일전=0, 오늘=29(최대)
    const idx = Math.min(30 - daysAgo, 29);
    chars[Math.max(idx, 0)] = ':';
  }

  return chars;
}

/**
 * 클릭한 마커가 다른 마커들과 겹치지 않을 최소 줌 레벨 반환
 *
 * - 현재 줌에서 이미 겹치지 않으면 null 반환 (줌 변경 없이 패닝만)
 * - 겹치는 마커가 있으면 모두 떨어질 만큼의 최소 줌 반환
 *
 * 겹침 판단: 두 마커 픽셀 거리 < 반지름 합 + 4px 여백
 *
 * @param {L.Map}  map           Leaflet 맵 인스턴스
 * @param {object} clickedRegion 클릭된 지역 데이터 (avg_lat, avg_lng, visit_days_in_period, region)
 * @param {object[]} allRegions  전체 지역 데이터 배열
 * @returns {number|null} 최소 줌 레벨 또는 null
 */
export function findMinZoomToSeparate(map, clickedRegion, allRegions) {
  const clat  = parseFloat(clickedRegion.avg_lat);
  const clng  = parseFloat(clickedRegion.avg_lng);
  const cR    = getMarkerSize(Number(clickedRegion.visit_days_in_period)) / 2;

  // 좌표가 있는 다른 지역만 대상
  const others = allRegions.filter((r) => {
    if (r.region === clickedRegion.region) return false;
    return parseFloat(r.avg_lat) && parseFloat(r.avg_lng);
  });

  // 특정 줌 레벨에서 클릭 마커와 겹치는 이웃이 있는지 확인
  function overlapsAtZoom(zoom) {
    const cp = map.project([clat, clng], zoom);
    for (const o of others) {
      const oR = getMarkerSize(Number(o.visit_days_in_period)) / 2;
      const op = map.project([parseFloat(o.avg_lat), parseFloat(o.avg_lng)], zoom);
      const dist = Math.sqrt((cp.x - op.x) ** 2 + (cp.y - op.y) ** 2);
      if (dist < cR + oR + 4) return true; // 4px 여백
    }
    return false;
  }

  const currentZoom = Math.round(map.getZoom());
  // 현재 줌에서 이미 겹치지 않으면 줌 변경 불필요
  if (!overlapsAtZoom(currentZoom)) return null;

  // 겹침이 사라질 최소 줌을 찾아 반환
  const maxZoom = map.getMaxZoom() || 18;
  for (let z = currentZoom + 1; z <= maxZoom; z++) {
    if (!overlapsAtZoom(z)) return z;
  }
  return maxZoom;
}
